/**
 * Session timeout manager — auto-logout after configurable inactivity period.
 * Client-side only.
 */

const SESSION_TIMEOUT_KEY = 'lcm_session_timeout_ms';
const LAST_ACTIVITY_KEY = 'lcm_last_activity';
const DEFAULT_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

export function getSessionTimeoutMs(): number {
  if (typeof window === 'undefined') return DEFAULT_TIMEOUT_MS;
  const stored = localStorage.getItem(SESSION_TIMEOUT_KEY);
  return stored ? parseInt(stored, 10) : DEFAULT_TIMEOUT_MS;
}

export function setSessionTimeoutMs(ms: number): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(SESSION_TIMEOUT_KEY, String(ms));
}

export function updateLastActivity(): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
}

export function getLastActivity(): number {
  if (typeof window === 'undefined') return Date.now();
  const stored = localStorage.getItem(LAST_ACTIVITY_KEY);
  return stored ? parseInt(stored, 10) : Date.now();
}

export function isSessionExpired(): boolean {
  const timeout = getSessionTimeoutMs();
  const lastActivity = getLastActivity();
  return Date.now() - lastActivity > timeout;
}

let activityTimer: ReturnType<typeof setInterval> | null = null;
let onExpireCallback: (() => void) | null = null;

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

function handleActivity() {
  updateLastActivity();
}

export function startSessionMonitor(onExpire: () => void): void {
  if (typeof window === 'undefined') return;
  onExpireCallback = onExpire;
  updateLastActivity();

  ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, handleActivity, { passive: true }));

  if (activityTimer) clearInterval(activityTimer);
  activityTimer = setInterval(() => {
    if (isSessionExpired() && onExpireCallback) {
      stopSessionMonitor();
      onExpireCallback();
    }
  }, 60 * 1000); // Check every minute
}

export function stopSessionMonitor(): void {
  if (activityTimer) {
    clearInterval(activityTimer);
    activityTimer = null;
  }
  ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, handleActivity));
  onExpireCallback = null;
}

export const SESSION_TIMEOUT_OPTIONS = [
  { label: '15 minuti', value: 15 * 60 * 1000 },
  { label: '30 minuti', value: 30 * 60 * 1000 },
  { label: '1 ora', value: 60 * 60 * 1000 },
  { label: '2 ore', value: 2 * 60 * 60 * 1000 },
  { label: 'Mai', value: 0 },
];
